// WMS-R (2000) Nonverbale Merkspanne (Blockspanne / block-tapping span)
// Looks up PC for the "score global" total raw score (Gesamtrohwert), not
// the "score d'empan" (highest span reached, which has no norm table and is
// only recorded for reference).
//
// Norms: mean (M) and SD per age group (src/db/WMS-R-EMPANS/blockspan-norms.parquet).
// PC = normcdf((rawScore - M) / SD) * 100.

import { makeParquetQuerier } from "./parquetQuery";

const queryParquet = makeParquetQuerier("WMS-R-EMPANS");

// Standard normal CDF (Abramowitz & Stegun approximation) — same formula
// used elsewhere in this codebase (wais-scoring.js, calcStroopVictoria.js).
function normcdf(z) {
  if (z < -6) return 0;
  if (z > 6) return 1;
  const a1 = 0.254829592, a2 = -0.284496736, a3 = 1.421413741, a4 = -1.453152027, a5 = 1.061405429, p = 0.3275911;
  const sign = z < 0 ? -1 : 1;
  const x = Math.abs(z) / Math.SQRT2;
  const t = 1 / (1 + p * x);
  const erf = 1 - ((((a5 * t + a4) * t + a3) * t + a2) * t + a1) * t * Math.exp(-x * x);
  return 0.5 * (1 + sign * erf);
}

/**
 * Look up the WMS-R Blockspanne PC for a given age, direction, and score global.
 * @param {number} age - Patient age in years
 * @param {string|number} scoreGlobal - Total raw score (Gesamtrohwert): 0–14 (direct) or 0–12 (inverse)
 * @param {"direct"|"inverse"} kind
 * @param {string} lang
 * @returns {Promise<{pc: number, z: number} | {error: string}>}
 */
export async function calcWMSRBlockspanPC(age, scoreGlobal, kind, lang) {
  if (age == null) {
    return { error: lang === "de" ? "Geburtsdatum fehlt" : "Date de naissance manquante" };
  }

  const raw = parseFloat(scoreGlobal);
  if (!Number.isFinite(raw)) {
    return { error: lang === "de" ? "Ungültiger Rohwert" : "Score brut invalide" };
  }

  const rows = await queryParquet("blockspan-norms.parquet");
  const row = rows.find((r) => age >= r.age_min && age <= r.age_max);
  if (!row) {
    return {
      error: lang === "de"
        ? `Alter außerhalb der Normtabelle (15–74): ${age}`
        : `Âge hors tableau de normes (15–74) : ${age}`,
    };
  }

  const m = kind === "direct" ? row.m_vorwarts : row.m_ruckwarts;
  const sd = kind === "direct" ? row.sd_vorwarts : row.sd_ruckwarts;
  if (m == null || sd == null) {
    return { error: lang === "de" ? "Keine Norm gefunden" : "Norme introuvable" };
  }

  const z = (raw - m) / sd;
  const pc = Math.round(normcdf(z) * 100);
  return { pc, z: Math.round(z * 100) / 100 };
}
